import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import {
  Upload,
  Download,
  FileText,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  History,
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { ImportBatch, RegistrarAccount, ApiResponse } from '../types';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { format } from 'date-fns';

export default function ImportPage() {
  const { fetchApi, token } = useApi();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imports, setImports] = useState<ImportBatch[]>([]);
  const [accounts, setAccounts] = useState<RegistrarAccount[]>([]);
  const [defaultAccountId, setDefaultAccountId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState<{
    success: boolean;
    message: string;
    errors?: Array<{ row: number; domain?: string; message: string }>;
  } | null>(null);

  useEffect(() => {
    loadImports();
    loadAccounts();
  }, []);

  async function loadAccounts() {
    try {
      const res = await fetchApi<ApiResponse<RegistrarAccount[]>>('/accounts');
      if (res.data) setAccounts(res.data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  }

  async function loadImports() {
    try {
      const res = await fetchApi<ApiResponse<ImportBatch[]>>('/import/history');
      if (res.data) setImports(res.data);
    } catch (error) {
      console.error('Failed to load imports:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setLastResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (defaultAccountId) {
        formData.append('default_account_id', defaultAccountId);
      }

      const response = await fetch('/api/import/csv', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setLastResult({
          success: true,
          message: `Imported ${data.data.success_count} of ${data.data.total_rows} domains`,
          errors:
            data.data.error_count > 0 ? data.data.errors : undefined,
        });
        loadImports();
      } else {
        setLastResult({
          success: false,
          message: data.error || 'Import failed',
        });
      }
    } catch (error) {
      setLastResult({
        success: false,
        message: error instanceof Error ? error.message : 'Import failed',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`/api/import/template?token=${token}`, '_blank');
  };

  return (
    <div>
      <div className="mb-6">
        <PageHeader title="Import / Export" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Import card */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-brand-100 rounded-lg">
              <Upload className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Import Domains
              </h2>
              <p className="text-sm text-slate-500">
                Upload a CSV file to import domains
              </p>
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="default-account" className="block text-xs font-medium text-slate-600 mb-1">
              Link imported domains to account <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <select
              id="default-account"
              value={defaultAccountId}
              onChange={e => setDefaultAccountId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              disabled={uploading}
            >
              <option value="">— No specific account (registrar='manual') —</option>
              {accounts
                .slice()
                .sort((a, b) =>
                  a.registrar_name.localeCompare(b.registrar_name) ||
                  a.label.localeCompare(b.label)
                )
                .map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.registrar_name} — {acc.label}
                    {acc.username_hint ? ` (${acc.username_hint})` : ''}
                  </option>
                ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              Rows with an explicit <span className="font-mono">registrar</span> column override this default.
            </p>
          </div>

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className={`cursor-pointer ${uploading ? 'pointer-events-none' : ''}`}
            >
              {uploading ? (
                <RefreshCw className="h-8 w-8 text-brand-600 animate-spin mx-auto mb-2" />
              ) : (
                <FileText className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              )}
              <p className="text-slate-600">
                {uploading ? 'Uploading...' : 'Click to select a CSV file'}
              </p>
              <p className="text-sm text-slate-400 mt-1">or drag and drop</p>
            </label>
          </div>

          <button
            onClick={handleDownloadTemplate}
            className="mt-4 text-sm text-brand-600 hover:text-brand-800"
          >
            Download CSV template
          </button>

          {lastResult && (
            <div
              className={`mt-4 p-4 rounded-lg ${
                lastResult.success ? 'bg-green-50' : 'bg-red-50'
              }`}
            >
              <div className="flex items-center gap-2">
                {lastResult.success ? (
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span
                  className={
                    lastResult.success ? 'text-green-700' : 'text-red-700'
                  }
                >
                  {lastResult.message}
                </span>
              </div>
              {lastResult.errors && lastResult.errors.length > 0 && (
                <div className="mt-3 max-h-32 overflow-y-auto">
                  <p className="text-sm font-medium text-red-700 mb-1">
                    Errors:
                  </p>
                  {lastResult.errors.slice(0, 10).map((err, i) => (
                    <p key={i} className="text-xs text-red-600">
                      Row {err.row}
                      {err.domain && ` (${err.domain})`}: {err.message}
                    </p>
                  ))}
                  {lastResult.errors.length > 10 && (
                    <p className="text-xs text-red-600">
                      ... and {lastResult.errors.length - 10} more errors
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Export card */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Download className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Export Domains
              </h2>
              <p className="text-sm text-slate-500">
                Download all domains as CSV
              </p>
            </div>
          </div>

          <p className="text-slate-600 mb-4">
            Exports come from the Domains list: set the filters you want, then use <strong>Export</strong> there —
            it downloads exactly the domains the filters show (admins only).
          </p>

          <Link to="/domains" className="btn btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Go to Domains
          </Link>
        </div>
      </div>

      {/* CSV format info */}
      <div className="card p-6 mb-8">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          CSV Format
        </h3>
        <p className="text-slate-600 mb-4">
          Your CSV file should include the following columns:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="table-header">Column</th>
                <th className="table-header">Required</th>
                <th className="table-header">Description</th>
                <th className="table-header">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="table-cell font-mono">domain_name</td>
                <td className="table-cell text-emerald-600">Yes</td>
                <td className="table-cell">The domain name</td>
                <td className="table-cell font-mono">example.com</td>
              </tr>
              <tr>
                <td className="table-cell font-mono">registrar</td>
                <td className="table-cell text-slate-400">No</td>
                <td className="table-cell">Registrar name</td>
                <td className="table-cell font-mono">GoDaddy</td>
              </tr>
              <tr>
                <td className="table-cell font-mono">expiry_date</td>
                <td className="table-cell text-slate-400">No</td>
                <td className="table-cell">Expiration date (YYYY-MM-DD)</td>
                <td className="table-cell font-mono">2025-12-31</td>
              </tr>
              <tr>
                <td className="table-cell font-mono">auto_renew</td>
                <td className="table-cell text-slate-400">No</td>
                <td className="table-cell">true/false or yes/no</td>
                <td className="table-cell font-mono">true</td>
              </tr>
              <tr>
                <td className="table-cell font-mono">nameservers</td>
                <td className="table-cell text-slate-400">No</td>
                <td className="table-cell">Semicolon-separated list</td>
                <td className="table-cell font-mono">ns1.example.com;ns2.example.com</td>
              </tr>
              <tr>
                <td className="table-cell font-mono">tags</td>
                <td className="table-cell text-slate-400">No</td>
                <td className="table-cell">Semicolon-separated tag names</td>
                <td className="table-cell font-mono">production;important</td>
              </tr>
              <tr>
                <td className="table-cell font-mono">notes</td>
                <td className="table-cell text-slate-400">No</td>
                <td className="table-cell">Additional notes</td>
                <td className="table-cell font-mono">Main company website</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Import history */}
      <div className="card">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Import History</h3>
        </div>

        {loading ? (
          <table className="w-full">
            <tbody>
              <SkeletonRows rows={4} cols={6} />
            </tbody>
          </table>
        ) : imports.length === 0 ? (
          <EmptyState compact icon={History} title="No imports yet" description="CSV imports you run will be listed here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">File</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Total</th>
                  <th className="table-header">Success</th>
                  <th className="table-header">Errors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {imports.map((batch) => (
                  <tr key={batch.id}>
                    <td className="table-cell text-slate-500">
                      {format(new Date(batch.imported_at), 'MMM d, yyyy h:mm a')}
                    </td>
                    <td className="table-cell font-mono text-sm">
                      {batch.filename || '-'}
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={batch.status} />
                    </td>
                    <td className="table-cell">{batch.row_count}</td>
                    <td className="table-cell text-emerald-600">
                      {batch.success_count}
                    </td>
                    <td className="table-cell text-red-600">
                      {batch.error_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string; icon: typeof CheckCircle }> = {
    completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    processing: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: RefreshCw },
    pending: { bg: 'bg-slate-100', text: 'text-slate-700', icon: AlertCircle },
  };

  const style = styles[status] || styles.pending;
  const Icon = style.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
    >
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
}
